import React, { useState, useEffect } from 'react';
import { EnquiryFormData } from '../../../../backend/src/shared/types'; 
import { sendEnquiryEmail } from '../../api-client';
import { useNavigate } from 'react-router-dom';

interface EnquireNowModalProps {
  closeModal: () => void;
  hotelName: string;
  slug: string;
}

const EnquireNowModal: React.FC<EnquireNowModalProps> = ({ closeModal, hotelName, slug }) => {
  const [formData, setFormData] = useState<EnquiryFormData>({
    fullName: '',
    email: '',
    phoneNumber: '',
    departureDate: '',
    returnDate: '',
    nights: '',
    travelers: '',
    adults: '',
    children: '0',
    message: '',
    destination: hotelName,
    slug: slug,
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
      travelers: name === 'adults' || name === 'children' ? `${Number(prevFormData.adults) + Number(prevFormData.children)}` : prevFormData.travelers,
    }));
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.fullName.trim()) newErrors.fullName = 'Full Name is required';
    if (!formData.email.trim()) newErrors.email = 'E-Mail is required';
    if (!/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(formData.email)) newErrors.email = 'Invalid E-Mail format';
    if (!formData.phoneNumber.trim()) newErrors.phoneNumber = 'Phone Number is required';
    if (!/^\d{10,15}$/.test(formData.phoneNumber)) newErrors.phoneNumber = 'Invalid Phone Number format';
    if (!formData.departureDate) newErrors.departureDate = 'Departure Date is required';
    if (!formData.returnDate) newErrors.returnDate = 'Return Date is required';
    if (formData.returnDate && formData.departureDate && new Date(formData.returnDate) <= new Date(formData.departureDate)) {
      newErrors.returnDate = 'Return Date must be after Departure Date';
    }
    if (!formData.nights || Number(formData.nights) <= 0) newErrors.nights = 'Nights should be greater than 0';
    if (!formData.travelers || Number(formData.travelers) <= 0) newErrors.travelers = 'Travelers should be greater than 0';
    if (!formData.adults || Number(formData.adults) <= 0) newErrors.adults = 'Adults should be greater than 0';
    if (!formData.children) newErrors.children = 'Children is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      setLoading(true);
      try {
        await sendEnquiryEmail(formData); 
        navigate('/thank-you');
        alert('Message sent successfully!');
        setFormData({
          fullName: '',
          email: '',
          phoneNumber: '',
          departureDate: '',
          returnDate: '',
          nights: '',
          travelers: '',
          adults: '',
          children: '0',
          message: '',
          destination: '',
          slug: '',
        });
      } catch (error) {
        alert('Failed to send message');
        console.error('Error sending message:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (formData.departureDate) {
      const minReturnDate = new Date(formData.departureDate);
      minReturnDate.setDate(minReturnDate.getDate() + 1);
      setFormData((prevFormData) => ({
        ...prevFormData,
        returnDate: new Date(prevFormData.returnDate) < minReturnDate ? '' : prevFormData.returnDate,
      }));
    }
  }, [formData.departureDate]);

  useEffect(() => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      travelers: `${Number(prevFormData.adults) + Number(prevFormData.children)}`,
    }));
  }, [formData.adults, formData.children]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black bg-opacity-50"></div>
      <div className="relative bg-white rounded-lg shadow-lg md:w-[50%] md:h-[95%] 2xl:h-[75%] overflow-auto p-6 scrollbar-initial">
        <button className="absolute top-2 right-2 text-gray-500 hover:text-gray-700" onClick={closeModal}>
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <h2 className="text-2xl font-bold text-center mb-2">World Wonders Wait</h2>
        <p className="text-center text-gray-600 mb-4">Your Inquiry is the Key</p>
        <form onSubmit={handleSubmit}>
          <div className="mb-2">
            <label className="block text-gray-700">Full Name:</label>
            <input
              type="text"
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              className="w-full border rounded-lg px-3 py-2 mt-1 focus:outline-none focus:ring-2 focus:ring-pink-500"
            />
            {errors.fullName && <p className="text-red-500 text-sm">{errors.fullName}</p>}
          </div>
          <div className='mb-2 flex space-x-2'>
            <div className="flex-1">
              <label className="block text-gray-700">e-Mail:</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full border rounded-lg px-3 py-2 mt-1 focus:outline-none focus:ring-2 focus:ring-pink-500"
              />
              {errors.email && <p className="text-red-500 text-sm">{errors.email}</p>}
            </div>
            <div className="flex-1">
              <label className="block text-gray-700">Phone Number:</label>
              <input
                type="text"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleChange}
                className="w-full border rounded-lg px-3 py-2 mt-1 focus:outline-none focus:ring-2 focus:ring-pink-500"
              />
              {errors.phoneNumber && <p className="text-red-500 text-sm">{errors.phoneNumber}</p>}
            </div>
          </div>
          <div className='mb-2 flex space-x-2'>
            <div className="flex-1">
              <label className="block text-gray-700">Departure Date:</label>
              <input
                type="date"
                name="departureDate"
                value={formData.departureDate}
                onChange={handleChange}
                className="w-full border rounded-lg px-3 py-2 mt-1 focus:outline-none focus:ring-2 focus:ring-pink-500"
              />
              {errors.departureDate && <p className="text-red-500 text-sm">{errors.departureDate}</p>}
            </div>
            <div className="flex-1">
              <label className="block text-gray-700">Return Date:</label>
              <input
                type="date"
                name="returnDate"
                value={formData.returnDate}
                min={formData.departureDate ? new Date(new Date(formData.departureDate).setDate(new Date(formData.departureDate).getDate() + 1)).toISOString().split('T')[0] : ''}
                onChange={handleChange}
                className="w-full border rounded-lg px-3 py-2 mt-1 focus:outline-none focus:ring-2 focus:ring-pink-500"
              />
              {errors.returnDate && <p className="text-red-500 text-sm">{errors.returnDate}</p>}
            </div>
          </div>
          <div className="mb-2 flex space-x-2">
            <div className="flex-1">
              <label className="block text-gray-700">Nights:</label>
              <input
                type="number"
                name="nights"
                value={formData.nights}
                onChange={handleChange}
                className="w-full border rounded-lg px-3 py-2 mt-1 focus:outline-none focus:ring-2 focus:ring-pink-500"
              />
              {errors.nights && <p className="text-red-500 text-sm">{errors.nights}</p>}
            </div>
            <div className="flex-1">
              <label className="block text-gray-700">Adults:</label>
              <input
                type="number"
                name="adults"
                value={formData.adults}
                onChange={handleChange}
                className="w-full border rounded-lg px-3 py-2 mt-1 focus:outline-none focus:ring-2 focus:ring-pink-500"
              />
              {errors.adults && <p className="text-red-500 text-sm">{errors.adults}</p>}
            </div>
          </div>
          <div className="mb-2 flex space-x-2">
            <div className="flex-1">
              <label className="block text-gray-700">Children:</label>
              <input
                type="number"
                name="children"
                value={formData.children}
                onChange={handleChange}
                className="w-full border rounded-lg px-3 py-2 mt-1 focus:outline-none focus:ring-2 focus:ring-pink-500"
              />
              {errors.children && <p className="text-red-500 text-sm">{errors.children}</p>}
            </div>
            <div className="flex-1">
              <label className="block text-gray-700">Travelers:</label>
              <input
                type="number"
                name="travelers"
                value={formData.travelers}
                readOnly
                className="w-full border rounded-lg px-3 py-2 mt-1 focus:outline-none bg-gray-100 cursor-not-allowed"
              />
              {errors.travelers && <p className="text-red-500 text-sm">{errors.travelers}</p>}
            </div>
          </div>
          <div className="mb-4">
            <label className="block text-gray-700">Message:</label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              className="w-full border rounded-lg px-3 py-2 mt-1 focus:outline-none focus:ring-2 focus:ring-pink-500"
            ></textarea>
          </div>
          <input name='destination' type='hidden' value={formData.destination} onChange={handleChange}/>
          <input name='slug' type='hidden' value={formData.slug} onChange={handleChange}/>
          <button
            type="submit"
            className="w-full bg-pink-600 text-white py-2 rounded-lg font-semibold hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500"
            disabled={loading}
          >
            {loading ? 'Sending...' : 'Submit'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default EnquireNowModal;
